import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  PencilToLine,
  ArrowLeft,
  Ellipsis,
  CircleExclamation,
  TrashBin,
  Check,
  Xmark,
  CircleInfo,
} from "@gravity-ui/icons";
import Modal from "../modal/Modal";
import InputWithButton from "../generic_components/InputWithButton";
import { useLocalState } from "../../contexts/LocalStateProvider";
import apiService from "../../api/apiService"; // Import apiService
import { useNavigate } from "react-router-dom";
import { usePostHog } from "posthog-js/react";
import Toast from "../generic_components/Toast";
import * as Sentry from "@sentry/react"

const Header = ({
  projectName,
  lastEdited,
  onProjectNameChange,
  projectId,
  loading,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedProjectName, setEditedProjectName] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { updateLocalState } = useLocalState();
  const { localState } = useLocalState();
  const [pageWidth, setPageWidth] = useState(window.innerWidth);
  const posthog = usePostHog();
  const [toastMessage, setToastMessage] = useState(null);
  const [toastType, setToastType] = useState("info");
  const [isWaitingForStop, setIsWaitingForStop] = useState(false);

  const isProjectStarred = localState.cachedProjects?.find(
    (p) => p.project_id === Number(projectId)
  )?.is_starred;

  const navigate = useNavigate();

  const confirmAndStopGeneration = useCallback(() => {
    if (
      window.confirm(
        "Music generation is in progress. Are you sure you want to go back? This will stop the generation."
      )
    ) {
      setIsWaitingForStop(true);
      updateLocalState("stopGeneration", true);
    } else {
      // If user cancels on a popstate-triggered confirm, we need to push them forward again
      // to 'cancel the cancel'.
      window.history.pushState(null, document.title, window.location.href);
    }
  }, [updateLocalState]);

  useEffect(() => {
    if (isWaitingForStop && !localState.responseInProgress) {
      setIsWaitingForStop(false);
      navigate(-1);
    }
  }, [isWaitingForStop, localState.responseInProgress, navigate]);

  useEffect(() => {
    const handleResize = () => {
      setPageWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleBackClick = useCallback(() => {
    if (localState.responseInProgress) {
      confirmAndStopGeneration();
    } else {
      navigate(-1); // This will take the user to the previous page
    }
  }, [localState.responseInProgress, navigate, confirmAndStopGeneration]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const isNavBack =
        (event.altKey && event.key === "ArrowLeft") || // Windows
        (event.metaKey && (event.key === "ArrowLeft" || event.key === "[")); // Mac

      if (isNavBack && localState.responseInProgress) {
        event.preventDefault();
        confirmAndStopGeneration();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [localState.responseInProgress, confirmAndStopGeneration]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (localState.responseInProgress) {
        event.preventDefault();
        event.returnValue =
          "Music generation is in progress. Are you sure you want to leave? This will stop the generation.";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [localState.responseInProgress]);

  useEffect(() => {
    const handlePopState = () => {
      if (localState.responseInProgress) {
        // The URL has already changed when this event fires.
        // We need to handle this differently for browser back button
        if (window.confirm(
          "Music generation is in progress. Are you sure you want to go back? This will stop the generation."
        )) {
          // User confirmed - trigger stop generation and clean up state immediately
          updateLocalState("stopGeneration", true);
          updateLocalState("responseInProgress", false);
          // Don't prevent navigation since user confirmed
        } else {
          // User cancelled - push forward to undo the navigation
          window.history.pushState(null, document.title, window.location.href);
        }
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [localState.responseInProgress, updateLocalState]);

  const handleEditClick = () => {
    posthog.capture("Click on Edit Project Name Icon Button", {
      is_user_logged_in: true
    });
    setIsEditing(true);
  };

  useEffect(() => {
    if (!editedProjectName && projectName && !isEditing) {
      setEditedProjectName(projectName);
    }
  }, [projectName]); // Dependencies

  useEffect(() => {
    if (localState.projectNameUpdated) {
      setEditedProjectName(localState.projectName);
      updateLocalState("projectNameUpdated", false);
    }
  }, [localState.projectNameUpdated, localState.projectName]);

  const handleSaveClick = async () => {
    posthog.capture("Click on Save Project Name Icon Button", {
      is_user_logged_in: true
    });
    if (editedProjectName !== projectName) {
      try {
        const payload = {
          projectId,
          project_name: editedProjectName,
          set_by_user: true,
        };
        await apiService.sendRequest("updateProjectName", payload);

        updateLocalState("projectNameSetByUser", true);

        // Call onProjectNameChange to update the parent component with the new name
        if (onProjectNameChange) {
          onProjectNameChange(editedProjectName);
        }
      } catch (error) {
        console.error("Failed to update project name:", error);
        Sentry.captureException(error);
      }
    }
    setIsEditing(false);
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    setEditedProjectName(projectName); // Revert to original name
  };

  const handleDropdownToggle = () => {
    setShowDropdown(!showDropdown);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setShowDropdown(false);
    }
  };

  useEffect(() => {
    setEditedProjectName(projectName);

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleDeleteProject = () => {
    posthog.capture("Click on Delete Project", {
      is_user_logged_in: true
    });
    // posthog.capture("Chat Interface Delete Project Button Clicked");
    setShowDeleteModal(true);
  };

  const closeModal = () => {
    setShowDeleteModal(false);
  };

  const confirmDeleteProject = async () => {
    posthog.capture("Click on Confirm Delete Project Button", {
      is_user_logged_in: true
    });

    try {
      const payload = { projectId };
      await apiService.sendRequest("deleteProject", payload);

      // Update cached projects by filtering out the deleted project
      const updatedProjects = localState.cachedProjects.filter(
        (project) => Number(project.project_id) !== Number(projectId)
      );

      // Update both states atomically
      updateLocalState("cachedProjects", updatedProjects);
      updateLocalState("totalProjects", (prev) => prev - 1);

      // Remove liked tracks associated with this project
      const updatedLikedTracks = localState.likedProjectTracks?.filter(
        (track) => track.project_id !== projectId
      );
      updateLocalState("likedProjectTracks", updatedLikedTracks);

      posthog.capture("Project deleted successfully", {
        is_user_logged_in: true
      });

      setTimeout(() => {
        setToastMessage("Project deleted successfully");
        setToastType("success");

        // Add navigation to redirect back to projects page
        navigate("/projects");
      }, 1500);

      // Handle any additional logic after deletion, such as redirecting or updating state
    } catch (error) {
      console.error("Failed to delete project:", error);
      Sentry.captureException(error);
    } finally {
      setShowDeleteModal(false);
    }
  };

  const handleOpenDetailsPanel = () => {
    posthog.capture("Click to open Details Panel", {
      is_user_logged_in: true,
      project_id: projectId,
      project_name: editedProjectName,
      is_starred: isProjectStarred,
    });
    updateLocalState("isDetailsPanelOpen", true);
  };

  return (
    <div
      ref={dropdownRef}
      className="flex items-center relative px-4 py-3 gap-4"
    >
      <button
        id="back-button"
        onClick={handleBackClick}
        className="md:p-2 p-2.5 justify-center items-center bg-[#2c2a2c] rounded-full"
      >
        <ArrowLeft />
      </button>
      <div className="flex flex-1 justify-center items-center">
        {isEditing ? (
          <>
            {pageWidth <= 768 ? (
              <>
                <input
                  id="project-name-input"
                  type="text"
                  className="px-3 md:h-8 h-9 md:text-xs text-sm rounded-full border border-solid border-[#878188] bg-transparent text-white mx-1 w-full max-w-48"
                  value={editedProjectName}
                  onChange={(e) => setEditedProjectName(e.target.value)}
                />
                <button
                  id="cancel-edit-button"
                  className="mr-1 text-xs font-normal px-2 h-8 hover:[background:var(--Color-Opacity-White-5,rgba(255,255,255,0.05))] text-[#878188] hover:text-white rounded-full"
                  onClick={handleCancelClick}
                >
                  <Xmark />
                </button>
                <button
                  id="save-edit-button"
                  className="h-8 text-xs bg-white hover:bg-[#E0DFE1] px-2 text-black font-normal rounded-full"
                  onClick={handleSaveClick}
                >
                  <Check />
                </button>
              </>
            ) : (
              <>
                <input
                  id="project-name-input"
                  type="text"
                  className="px-2.5 h-8 text-xs rounded-full border border-solid border-[#878188] bg-transparent text-white mr-2 w-full max-w-48 focus:outline-none appearance-none focus:ring-0"
                  value={editedProjectName}
                  onChange={(e) => setEditedProjectName(e.target.value)}
                />
                <button
                  id="cancel-edit-button"
                  className="mr-1 text-xs font-normal px-2.5 h-8 hover:[background:var(--Color-Opacity-White-5,rgba(255,255,255,0.10))] text-[#878188] bg-white/5 hover:text-white rounded-full"
                  onClick={handleCancelClick}
                >
                  Cancel
                </button>
                <button
                  id="save-edit-button"
                  className="h-8 text-xs bg-white hover:bg-white/90 px-2.5 text-black font-normal rounded-full"
                  onClick={handleSaveClick}
                >
                  Save
                </button>
              </>
            )}
          </>
        ) : (
          <h1 className="text-lg font-normal line-clamp-1">
            {editedProjectName}
          </h1>
        )}
        <div className="flex items-center ml-1">
          {!loading && !isEditing && (
            <button
              id="edit-project-name-button"
              className="text-xl ml-2"
              onClick={handleEditClick}
            >
              <PencilToLine className="w-4 h-4 text-[#878188]" />
            </button>
          )}
        </div>
      </div>
      <div className="flex items-center ml-auto">
        <button
          id="dropdown-toggle-button"
          onClick={handleDropdownToggle}
          className="bg-[#2c2a2c] md:p-2 p-2.5 rounded-full"
        >
          <Ellipsis />
        </button>
        {showDropdown && (
          <div
            id="chatpage-project-details-dropdown"
            ref={dropdownRef}
            className="absolute right-3 top-14 text-center font-normal text-sm text-[#878188] bg-[#100f10] py-1 px-1 rounded-lg border border-solid border-[#2c2a2c] w-36 z-20"
          >
            <button
              id="view-details-button"
              onClick={handleOpenDetailsPanel}
              className="flex items-center justify-left gap-2 hover:text-white h-8 px-2 rounded-lg hover:bg-zinc-900 w-full"
            >
              <CircleInfo />
              View details
            </button>
            {!isProjectStarred && (
              <button
                id="delete-project-button"
                onClick={handleDeleteProject}
                className="flex items-center justify-left gap-2 h-8 hover:text-white px-2 rounded-lg hover:bg-zinc-900 w-full"
              >
                <TrashBin /> Delete project
              </button>
            )}
          </div>
        )}
        {showDeleteModal && (
          <Modal closeModal={closeModal}>
            <div className="text-white w-full pb-3 bg-[#1d1b1d] border border-solid border-[#3c393c] rounded-lg">
              <h3 className="text-left text-lg p-3">Delete project</h3>
              <div className="w-full border-t border-t-[#2d2a2d]"></div>
              <InputWithButton
                formClassName="space-y-4 w-full px-3"
                inputFields={[
                  {
                    type: "info",
                    content: ["Are you sure you want to delete this project?"],
                    subContent:
                      "This will delete all the generated music in this project. Do you want to proceed?",
                    className:
                      "text-white text-left text-base font-extralight mb-4 mt-3",
                  },
                ]}
                buttonClassName="px-3 mr-3 h-9 text-sm bg-red-600 hover:bg-[#BB2D2D] text-white rounded-full"
                buttonText="Delete"
                onButtonClick={confirmDeleteProject} // Call confirmDeleteProject when delete is clicked
                cancelButtonClassName="px-3 h-9 text-sm rounded-full hover:[background:var(--Color-Opacity-White-5,rgba(255,255,255,0.10))] text-[#878188] hover:text-white bg-white/5"
                cancelButtonText="Cancel"
                onCancelClick={closeModal}
                closeModal={closeModal}
                cancelButtonId={"delete-project-cancel-button"}
                actionButtonId={"delete-project-confirm-button"}
                requestData={{
                  taskName: "deleteProject",
                  payload: {
                    project_id: projectId,
                  },
                }}
              />
            </div>
          </Modal>
        )}
      </div>
      {toastMessage && (
        <Toast
          type={toastType}
          message={toastMessage}
          onClose={() => setToastMessage("")}
        />
      )}
    </div>
  );
};

export default Header;
